import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  IoPersonOutline,
  IoSettingsOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoCarOutline,
  IoAddCircleOutline,
  IoNotifications,
  IoSearchOutline,
} from "react-icons/io5";
import {FiLogOut} from "react-icons/fi"
import { GoHome } from "react-icons/go";
import { useAuth } from "../../auth/AuthProvider";
import NotificationModal from "../notifications/NotificationModal";
import Api from "../../axiosConfig";

const navigation = [
  { name: "Home", href: "/user/home", icon: GoHome },
  { name: "My Deliveries", href: "/deliveries", icon: IoCarOutline },
  { name: "Request Delivery", href: "/order-delivery", icon: IoAddCircleOutline },
  {
    name: "Account Settings",
    href: "/account-settings",
    icon: IoSettingsOutline,
  },
];

const UserLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
  
      const response = await Api.get("/v1/notifications");
     
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.isRead).length);
    } catch (error) {
    
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    console.log('Mark as read:', notificationId);
    try {
      await Api.patch(`/v1/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n._id === notificationId ? { ...n, isRead: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
   
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId) => {
    console.log('Delete notification:', notificationId);
    try {
      await Api.delete(`/v1/notifications/${notificationId}`);
      const updatedNotifications = notifications.filter(n => n._id !== notificationId);
      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.filter(n => !n.isRead).length);
    } catch (error) {
   
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Set up polling for new notifications
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
  };

  const userNavigation = [
    { name: "Your Profile", href: "/profile" },
    { name: "Settings", href: "/account-settings" },
  ];

  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? "block" : "hidden"
        }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-full flex-col bg-white pt-5 pb-4 sm:max-w-xs">
          <div className="px-4 flex items-center justify-between">
            <Link to="/">            <img
              className="h-8 w-auto"
              src="/img/impact.webp"
              alt="Impact Logistics"
            />
            </Link>
            <button
              type="button"
              className="-mr-2 inline-flex h-10 w-10 items-center justify-center  bg-white p-2 text-gray-400 hover:bg-gray-100 focus:outline-none"
              onClick={() => setSidebarOpen(false)}
            >
              <IoCloseOutline className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-5 flex flex-1 flex-col overflow-y-auto">
            <nav className="flex-1 space-y-1 px-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      location.pathname === item.href
                        ? "bg-primary-dark text-white"
                        : "text-gray-600 hover:bg-gray-50"
                    } group flex items-center px-3 py-2 text-sm font-medium`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="mr-3 h-6 w-6 flex-shrink-0" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col ${
          isMinimized ? "lg:w-16" : "lg:w-64"
        } transition-all duration-300`}
      >
        <div className="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white">
          <div className="flex flex-1 flex-col pt-5 pb-4">
            <div className="flex items-center justify-between px-4">
              <Link to="/">
              {!isMinimized && (
                <img
                  className=" w-32"
                  src="/img/impact.webp"
                  alt="Impact Logistics"
                />
              )}
              </Link>
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 rounded-md text-gray-400 hover:bg-gray-100"
              >
                <IoMenuOutline className="h-6 w-6" />
              </button>
            </div>
            <nav className="mt-8 flex-1 space-y-1 px-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      location.pathname === item.href
                        ? "bg-primary-dark text-white"
                        : "text-gray-600 hover:bg-gray-50"
                    } group flex items-center  px-3 py-2 text-sm font-medium ${
                      isMinimized ? "justify-center" : ""
                    }`}
                  >
                    <Icon
                      className={`h-6 w-6 flex-shrink-0 ${
                        isMinimized ? "" : "mr-3"
                      }`}
                    />
                    {!isMinimized && item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
            {/* Logout Button */}
                  <div className="absolute bottom-4 w-full px-4">
                    <button
                      onClick={handleLogout}
                      className="
                        flex items-center w-full px-4 py-3 text-gray-700
                        hover:bg-gray-100 rounded-lg transition-colors duration-200
                      "
                    >
                      <FiLogOut className="h-5 w-5" />
                      <span className="ml-3">Logout</span>
                    </button>
                  </div>
        </div>
      </div>

      {/* Main content */}
      <div
        className={`lg:pl-64 transition-all duration-300 ${
          isMinimized ? "lg:pl-16" : ""
        }`}
      >
        {/* Top bar */}
        <div className="border-b">
          <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-whit max-w-5xl mx-auto">
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-dark lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <IoMenuOutline className="h-6 w-6" />
            </button>

            <div className="flex flex-1 justify-between px-4">
              <div className="flex flex-1">
                <div className="flex w-full md:ml-0">
                  <div className="relative w-full flex items-center max-w-2xl">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <IoSearchOutline className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search deliveries..."
                      className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-dark sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
              </div>

              <div className="ml-4 flex items-center md:ml-6">
                {/* Notification button */}
                <div className="relative">
                  <button
                    type="button"
                    className="rounded-full bg-white p-1 text-primary-dark hover:bg-gray-100"
                    onClick={() => setNotificationModalOpen(true)}
                  >
                    <IoNotifications className="h-6 w-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Notification Modal */}
                <NotificationModal
                  isOpen={notificationModalOpen}
                  onClose={() => setNotificationModalOpen(false)}
                  notifications={notifications}
                  onMarkAsRead={handleMarkAsRead}
                  onDeleteNotification={handleDeleteNotification}
                />

                {/* Profile dropdown */}
                <div className="relative ml-3" ref={dropdownRef}>
                  <button
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:ring-offset-2"
                  >
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={`${user.firstName}'s profile`}
                        className="h-8 w-8 rounded-full object-cover border border-primary-dark"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary-dark/10 flex items-center justify-center">
                        <IoPersonOutline className="h-4 w-4 text-primary-dark" />
                      </div>
                    )}
                  </button>

                  {/* Profile dropdown panel */}
                  {profileOpen && (
                    <div className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="px-4 py-2 text-sm text-gray-700">
                        <div className="font-medium truncate">
                          {user?.firstName} {user?.lastName}
                        </div>
                        <div className="text-gray-500 truncate text-xs">
                          {user?.email}
                        </div>
                      </div>
                      <div className="border-t border-gray-100">
                        {userNavigation.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setProfileOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Main content area */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserLayout;
